
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle,
  Users, Shield, Zap, GitBranch, 
  Calendar, DollarSign, BarChart3, Settings, Info
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";

interface TeamBuilderContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const TeamBuilderContent: React.FC<TeamBuilderContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "team-builder-guide") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team Builder Guide</h1>
          <p className="text-gray-500 mt-2">
            Learn how to effectively use the Team Builder feature to create, manage and optimize teams for your projects.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 2, 2023</span>
            <Badge variant="outline" className="ml-2">Enterprise</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="team-builder-overview">Team Builder Overview</h2>
          <p>
            The Team Builder is a powerful feature that enables project managers and team leads to assemble the optimal team for any project. 
            It provides intelligent recommendations based on skill matching, availability, past collaboration success, and project requirements.
          </p>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Team Builder Benefits
            </h3>
            <ul className="text-sm text-blue-700 space-y-1 pl-6 list-disc">
              <li>Reduce team assembly time by up to 70%</li>
              <li>Increase project success rates by 35%</li>
              <li>Optimize resource allocation across multiple projects</li>
              <li>Identify skill gaps before they impact project timelines</li>
              <li>Enhance team cohesion through compatibility matching</li>
            </ul>
          </div>

          <h2 id="key-features">Key Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <Users className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">AI-Powered Team Suggestions</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Receive intelligent team composition recommendations based on your project requirements, team member skills, availability, and past performance.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <Shield className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Role-Based Team Structure</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Define clear roles and responsibilities for each team member, ensuring balanced workload distribution and clear accountability.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <Calendar className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Availability Optimization</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Automatically consider team member availability and workload to avoid scheduling conflicts and prevent burnout.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <BarChart3 className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Performance Analytics</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Access detailed team performance metrics to identify improvement areas and track progress over time.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <DollarSign className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Budget Optimization</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Balance team skills with budget constraints to create cost-effective team structures without compromising quality.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-indigo-500">
              <CardContent className="pt-6">
                <div className="flex items-start mb-3">
                  <GitBranch className="h-5 w-5 text-indigo-500 mr-2 flex-shrink-0" />
                  <h3 className="font-medium">Team Templates</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Save successful team configurations as templates for similar future projects, accelerating the team creation process.
                </p>
              </CardContent>
            </Card>
          </div>

          <h2 id="getting-started">Getting Started with Team Builder</h2>
          
          <div className="my-6 space-y-6">
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Define Your Project Requirements</h3>
                <p className="text-gray-600 mb-3">
                  Start by outlining your project's specific needs, including required skills, timeline, budget, and team size.
                </p>
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <p className="text-gray-700 mb-2">
                    <strong>Pro Tip:</strong> Be as specific as possible about skill requirements to receive more accurate team recommendations.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Search for Team Members</h3>
                <p className="text-gray-600 mb-3">
                  Use the comprehensive search functionality to find talent with the specific skills your project requires.
                </p>
                <div className="grid grid-cols-1 gap-3">
                  <div className="border border-green-200 bg-green-50 p-3 rounded-md">
                    <p className="text-sm font-medium text-green-700 mb-1">Search Tips:</p>
                    <ul className="text-sm text-green-600 list-disc pl-5 space-y-1">
                      <li>Use skill-specific keywords to find specialized talent</li>
                      <li>Filter by availability to see only candidates who can start immediately</li>
                      <li>Filter by location for timezone-compatible team members</li>
                      <li>Review performance ratings and client feedback</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Build Your Team</h3>
                <p className="text-gray-600 mb-3">
                  Add selected members to your team, defining their roles, responsibilities, and time commitments.
                </p>
                <div className="bg-gray-50 p-3 rounded-md">
                  <h4 className="font-medium text-sm mb-2">Key Considerations:</h4>
                  <ul className="text-sm space-y-1">
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Ensure a balanced distribution of senior and junior talent</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Consider cultural and time zone compatibility for remote teams</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Align daily rates with your budget constraints</span>
                    </li>
                    <li className="flex items-center">
                      <Check className="h-4 w-4 text-emerald-500 mr-2" />
                      <span>Consider past collaboration history between team members</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Review Team Configuration</h3>
                <p className="text-gray-600 mb-3">
                  Analyze the proposed team composition to identify potential gaps or conflicts.
                </p>
                <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>Important:</strong> The Team Builder AI will highlight potential issues such as skill gaps, availability conflicts, or budget overruns.
                  </p>
                  <p className="text-sm text-amber-800">
                    Pay particular attention to the team compatibility score and make adjustments as needed.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                  5
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Finalize and Save</h3>
                <p className="text-gray-600 mb-3">
                  Once you're satisfied with your team composition, finalize the team and save it for your project.
                </p>
                <div className="bg-gray-50 p-3 rounded-md">
                  <h4 className="font-medium text-sm mb-2">Additional Options:</h4>
                  <ul className="text-sm space-y-1">
                    <li className="flex items-center">
                      <Zap className="h-4 w-4 text-amber-500 mr-2" />
                      <span>Save as a template for future similar projects</span>
                    </li>
                    <li className="flex items-center">
                      <Zap className="h-4 w-4 text-amber-500 mr-2" />
                      <span>Generate team introduction emails automatically</span>
                    </li>
                    <li className="flex items-center">
                      <Zap className="h-4 w-4 text-amber-500 mr-2" />
                      <span>Schedule a team kickoff meeting with calendar integration</span>
                    </li>
                    <li className="flex items-center">
                      <Zap className="h-4 w-4 text-amber-500 mr-2" />
                      <span>Create project-specific communication channels</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <h2 id="advanced-team-management">Advanced Team Management</h2>
          
          <Tabs defaultValue="team-analytics" className="w-full my-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="team-analytics">Team Analytics</TabsTrigger>
              <TabsTrigger value="resource-allocation">Resource Allocation</TabsTrigger>
              <TabsTrigger value="performance-tracking">Performance Tracking</TabsTrigger>
            </TabsList>
            <TabsContent value="team-analytics" className="p-4 border rounded-md mt-2">
              <h3 className="font-medium mb-3">Team Analytics</h3>
              <p className="text-gray-600 mb-4">
                Monitor team performance metrics to identify strengths, weaknesses, and areas for improvement.
              </p>
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Key Metrics:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-blue-500 mr-2">🔍</div>
                    <div>
                      <span className="font-medium">Productivity Index</span>
                      <p className="text-gray-500">Measures output relative to time invested</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-blue-500 mr-2">🔍</div>
                    <div>
                      <span className="font-medium">Collaboration Score</span>
                      <p className="text-gray-500">Evaluates effectiveness of team communication and cooperation</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-blue-500 mr-2">🔍</div>
                    <div>
                      <span className="font-medium">Skill Utilization Rate</span>
                      <p className="text-gray-500">Shows how effectively team members' skills are being utilized</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-blue-500 mr-2">🔍</div>
                    <div>
                      <span className="font-medium">Quality Metrics</span>
                      <p className="text-gray-500">Tracks errors, revisions, and client satisfaction</p>
                    </div>
                  </li>
                </ul>
              </div>
            </TabsContent>
            <TabsContent value="resource-allocation" className="p-4 border rounded-md mt-2">
              <h3 className="font-medium mb-3">Resource Allocation</h3>
              <p className="text-gray-600 mb-4">
                Optimize how team members' time and skills are allocated across project tasks.
              </p>
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Allocation Features:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-purple-500 mr-2">⚖️</div>
                    <div>
                      <span className="font-medium">Workload Balancing</span>
                      <p className="text-gray-500">Distribute tasks evenly to prevent burnout and bottlenecks</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-purple-500 mr-2">⚖️</div>
                    <div>
                      <span className="font-medium">Skill-Based Assignment</span>
                      <p className="text-gray-500">Match tasks to team members with the most relevant skills</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-purple-500 mr-2">⚖️</div>
                    <div>
                      <span className="font-medium">Resource Forecasting</span>
                      <p className="text-gray-500">Predict future resource needs based on project roadmap</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-purple-500 mr-2">⚖️</div>
                    <div>
                      <span className="font-medium">Cost Optimization</span>
                      <p className="text-gray-500">Allocate resources to maximize ROI while maintaining quality</p>
                    </div>
                  </li>
                </ul>
              </div>
            </TabsContent>
            <TabsContent value="performance-tracking" className="p-4 border rounded-md mt-2">
              <h3 className="font-medium mb-3">Performance Tracking</h3>
              <p className="text-gray-600 mb-4">
                Monitor individual and team performance against project goals and KPIs.
              </p>
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Tracking Tools:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-emerald-500 mr-2">📊</div>
                    <div>
                      <span className="font-medium">Performance Dashboards</span>
                      <p className="text-gray-500">Real-time visualization of key performance indicators</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-emerald-500 mr-2">📊</div>
                    <div>
                      <span className="font-medium">Goal Tracking</span>
                      <p className="text-gray-500">Monitor progress toward specific project objectives</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-emerald-500 mr-2">📊</div>
                    <div>
                      <span className="font-medium">Milestone Achievement</span>
                      <p className="text-gray-500">Track completion of critical project phases</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="h-5 w-5 text-emerald-500 mr-2">📊</div>
                    <div>
                      <span className="font-medium">Feedback Integration</span>
                      <p className="text-gray-500">Incorporate client and peer feedback into performance metrics</p>
                    </div>
                  </li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>

          <h2 id="team-templates">Using Team Templates</h2>
          <p>
            Team templates allow you to save successful team configurations for reuse on similar projects, 
            saving time and ensuring consistency in your team structures.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 my-6">
            <h3 className="font-medium mb-4">Template Management Best Practices</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-4 w-4" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium">Create Role-Based Templates</h4>
                  <p className="text-sm text-gray-600">
                    Define templates based on project roles rather than specific individuals to maintain flexibility.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-4 w-4" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium">Include Skills and Experience Requirements</h4>
                  <p className="text-sm text-gray-600">
                    Specify the necessary skills and experience level for each role in the template.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-4 w-4" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium">Document Team Structure</h4>
                  <p className="text-sm text-gray-600">
                    Include notes on reporting relationships, communication channels, and collaboration expectations.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-4 w-4" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium">Regularly Update Templates</h4>
                  <p className="text-sm text-gray-600">
                    Review and update templates based on project outcomes and lessons learned.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["team-builder-guide"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("team-builder-guide", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("team-builder-guide", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("team-builder-guide") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("team-builder-guide")}
            >
              {bookmarkedArticles.includes("team-builder-guide") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
